import { computePosition, offset, arrow, autoUpdate, size, flip } from '@floating-ui/vue'
import parseTooltipBindings from './parseTooltipBindings'
import { nextTick } from 'vue'

let delayShowFn = null

export const ReversedSide = {
  top: 'bottom',
  right: 'left',
  bottom: 'top',
  left: 'right'
}

/**
 * Shortcut for formatting insertion of text or HTML.
 */
const setInner = (el, config) => {
  const format = config.html ? 'innerHTML' : 'innerText'
  el[format] = config.title
}

/**
 * Find appropriate parent.
 */
const getScrollParent = (el) => {
  if (!el) {
    return null
  }

  if (el.scrollHeight > el.clientHeight) {
    return el
  } else {
    return getScrollParent(el.parentNode) || el
  }
}

const createTooltip = (event) => {
  if (event.target.tooltipElement) {
    return
  }

  const config = event.target.tooltipConfig || {}

  const detectOverflowOptions = {
    boundary: config.boundary === 'scrollParent' ? getScrollParent(event.target) : config.boundary
  }

  // Create tooltip element
  const tooltipElement = document.createElement('div')
  tooltipElement.classList.add('tooltip', 'b-tooltip')
  tooltipElement.setAttribute('role', 'tooltip')

  // Create inner element
  const tooltipTextElement = document.createElement('div')
  tooltipTextElement.classList.add('tooltip-inner')
  setInner(tooltipTextElement, config)
  tooltipElement.append(tooltipTextElement)

  // Create arrow element
  const arrowElement = document.createElement('div')
  arrowElement.classList.add('mz-ui-tooltip__arrow')
  tooltipElement.append(arrowElement)

  event.target.tooltipElement = tooltipElement
  document.body.append(tooltipElement)

  event.target.tooltipCleanup = autoUpdate(event.target, tooltipElement, async () => {
    const { x, y, middlewareData, placement } = await computePosition(event.target, tooltipElement, {
      placement: config.placement,
      middleware: [
        offset(5),
        size({
          apply({ availableWidth, availableHeight, elements }) {
            Object.assign(elements.floating.style, {
              maxWidth: `${availableWidth}px`,
              maxHeight: `${availableHeight}px`
            })
          }
        }),
        flip({
          fallbackStrategy: 'initialPlacement',
          ...detectOverflowOptions
        }),
        arrow({
          element: arrowElement
        })
      ]
    })

    tooltipElement.style.left = `${x}px`
    tooltipElement.style.top = `${y}px`

    if (middlewareData.arrow) {
      const { x: ax, y: ay } = middlewareData.arrow
      const side = placement.split('-')[0]

      arrowElement.style.left = ax !== null ? `${ax}px` : ''
      arrowElement.style.top = ay !== null ? `${ay}px` : ''
      arrowElement.style.right = ''
      arrowElement.style.bottom = ''
      arrowElement.style[ReversedSide[side]] = '-4px'
      arrowElement.style.transform = 'rotate(45deg'
    }

    if (config.interactive) {
      // Allow the tooltip to be hovered, copied, etc.
      const rect = tooltipElement.getBoundingClientRect()
      const boundaryPadding = 10
      const rectObject = {
        top: rect.top - boundaryPadding,
        left: rect.left - boundaryPadding,
        right: rect.right + boundaryPadding,
        bottom: rect.bottom + boundaryPadding
      }
      tooltipElement.rect = rectObject

      tooltipElement.addEventListener('mouseleave', () => {
        cleanup(event.target)
      })
    }
  })
}

/**
 * Remove tooltip from the DOM.
 */
function cleanup(el) {
  if (el && el.tooltipElement && el.tooltipCleanup) {
    el.tooltipCleanup()
    el.tooltipElement.remove()
    delete el.tooltipElement
    delete el.tooltipCleanup
  }
}

const isMouseInRect = (event, rect) => {
  var x = event.clientX
  if (x < rect.left || x >= rect.right) return false
  var y = event.clientY
  if (y < rect.top || y >= rect.bottom) return false
  return true
}

/**
 * Mouse over/focus
 */
const showEvent = (event) => {
  if (event.target?.tooltipConfig?.disabled) {
    return
  }
  const delayShow = event.target?.tooltipConfig?.delay?.show || 0
  clearTimeout(delayShowFn)
  delayShowFn = setTimeout(() => {
    createTooltip(event)
  }, delayShow)
}

/**
 * Mouse out/blur.  NOTE: We do not use the `hide` delay
 */
const hideEvent = (event) => {
  const isFocusEvent = event.constructor.name === 'FocusEvent'
  const inRect = event.target?.tooltipElement?.rect ? isMouseInRect(event, event.target.tooltipElement.rect) : false
  if (isFocusEvent || !inRect) {
    clearTimeout(delayShowFn)
    cleanup(event.target)
  }

}

/**
 * Attach listeners.
 */
const addListeners = (el, config) => {
  const triggers = config.trigger.split(' ')
  triggers.forEach(trigger => {
    if (trigger === 'click') {
      el.addEventListener('click', showEvent)
    } else if (trigger === 'focus') {
      el.addEventListener('focusin', showEvent)
      el.addEventListener('focusout', hideEvent)
    } else if (trigger === 'blur') {
      el.addEventListener('focusout', showEvent)
    } else if (trigger === 'hover') {
      el.addEventListener('mouseenter', showEvent)
      el.addEventListener('mouseleave', hideEvent)
    }
  })
}

/**
 * Detach listeners.
 */
const removeListeners = (el) => {
  const events = ['click', 'focusin', 'focusout', 'mouseenter', 'mouseleave']
  events.forEach(event => {
    try {
      el.removeEventListener(event, showEvent)
      el.removeEventListener(event, hideEvent)
    } catch (err) { }
  })
}

const VBTooltip = {
  beforeMount(el, binding, vnode) {
    const config = parseTooltipBindings(binding, vnode)
    if (config.title) {
      el.tooltipConfig = config
      addListeners(el, config)
    }
  },

  updated(el, binding, vnode) {
    if (el.tooltipElement) {
      nextTick(() => {
        const inner = el.tooltipElement.querySelector('.tooltip-inner')
        const config = parseTooltipBindings(binding, vnode)

        el.tooltipConfig = config

        if (inner) {
          setInner(inner, config)
        }
      })
    }
  },

  unmounted(el) {
    removeListeners(el)
    cleanup(el)
  }
}

export default VBTooltip
